/**
 * Pulsing Home Icon
 * Centralized animated home icon with synchronized background + icon color pulse.
 * Matches the landing page animation exactly.
 * Import this wherever the pulsing house icon is needed.
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { borderRadius } from '@config/spacing';
import { SmoothHouseIcon } from '@components/icons/smooth-house-icon';

const gentlePulse = `
  @keyframes gentlePulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
      background: ${colors.highlight.dark};
    }
    50% {
      transform: scale(1.05);
      opacity: 0.9;
      background: ${colors.brand.primary};
    }
  }
`;

const iconColorPulse = `
  @keyframes iconColorPulse {
    0%, 100% {
      color: ${colors.brand.primary};
    }
    50% {
      color: ${colors.highlight.dark};
    }
  }
`;

const IconContainer = styled.div<{ $size: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  background: ${colors.highlight.dark};
  border-radius: ${borderRadius.full};
  animation: gentlePulse 9s ease-in-out infinite;
  box-shadow: 0 12px 40px rgba(51, 65, 85, 0.35);
  ${gentlePulse}

  svg {
    width: 70%;
    height: 70%;
    color: ${colors.brand.primary};
    animation: iconColorPulse 9s ease-in-out infinite;
    ${iconColorPulse}
  }
`;

interface PulsingHomeIconProps {
  /** Circle diameter in pixels (default: 80) */
  size?: number;
}

export const PulsingHomeIcon: React.FC<PulsingHomeIconProps> = ({ size = 80 }) => (
  <IconContainer $size={size}>
    <SmoothHouseIcon />
  </IconContainer>
);
